/**
 * useFamilyBonding — Manages family bonding activity state (completed, dismissed, custom).
 */
import { useMemo, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { useScopedStorageKey } from './useScopedStorageKey';
import { useFamilyActivities } from './useFamilyActivities';
import { FamilyActivity, FamilyBondingState, DEFAULT_FAMILY_BONDING_STATE } from '@/types/familyBonding';

const getWeekStart = (): string => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(now);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday.toISOString().split('T')[0];
};

export function useFamilyBonding() {
  const storageKey = useScopedStorageKey('family_bonding');
  const { value: state, setValue: setState } = useLocalStorage<FamilyBondingState>(
    storageKey,
    DEFAULT_FAMILY_BONDING_STATE
  );

  const { data: dbActivities = [], isLoading } = useFamilyActivities();

  // Check if we need weekly reset
  const currentWeekStart = getWeekStart();
  const needsWeeklyReset = state.lastResetDate !== currentWeekStart;

  // Combine DB activities with custom activities
  const allActivities = useMemo(() => {
    const customActivities = state.customActivities.map(a => ({ ...a, isCustom: true }));
    return [...dbActivities, ...customActivities].filter(
      a => !state.dismissedActivities.includes(a.id)
    );
  }, [dbActivities, state.customActivities, state.dismissedActivities]);

  // Get completion status for current week
  const completedThisWeek = useMemo(() => {
    if (needsWeeklyReset) return [];
    return state.completedActivities;
  }, [state.completedActivities, needsWeeklyReset]);

  const completedCount = completedThisWeek.length;
  const totalCount = allActivities.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Toggle activity completion
  const toggleActivity = useCallback((id: string) => {
    setState(prev => {
      const isCompleted = prev.completedActivities.includes(id);
      const newCompleted = isCompleted
        ? prev.completedActivities.filter(a => a !== id)
        : [...prev.completedActivities, id];
      
      return {
        ...prev,
        completedActivities: newCompleted,
        lastResetDate: currentWeekStart, // Ensure week is tracked
      };
    });
  }, [setState, currentWeekStart]);

  // Add custom activity
  const addCustomActivity = useCallback((activity: Omit<FamilyActivity, 'id' | 'isCustom'>) => {
    const newActivity: FamilyActivity = {
      ...activity,
      id: `custom-${Date.now()}`,
      isCustom: true,
    };
    setState(prev => ({
      ...prev,
      customActivities: [...prev.customActivities, newActivity],
    }));
  }, [setState]);

  // Dismiss activity
  const dismissActivity = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      dismissedActivities: [...prev.dismissedActivities, id],
      completedActivities: prev.completedActivities.filter(a => a !== id),
    }));
  }, [setState]);

  // Remove custom activity
  const removeCustomActivity = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      customActivities: prev.customActivities.filter(a => a.id !== id),
      completedActivities: prev.completedActivities.filter(a => a !== id),
    }));
  }, [setState]);

  // Reset week (clears completed activities)
  const resetWeek = useCallback(() => {
    setState(prev => ({
      ...prev,
      completedActivities: [],
      lastResetDate: currentWeekStart,
    }));
  }, [setState, currentWeekStart]);

  // Mark feature as seen
  const markFeatureSeen = useCallback(() => {
    setState(prev => ({
      ...prev,
      hasSeenFeature: true,
    }));
  }, [setState]);

  // Get weekly summary (for previous week)
  const getWeeklySummary = useCallback(() => {
    return {
      completedCount: state.completedActivities.length,
      totalCount: allActivities.length,
      completedActivities: allActivities.filter(a => 
        state.completedActivities.includes(a.id)
      ),
    };
  }, [state.completedActivities, allActivities]);

  return {
    activities: allActivities,
    completedActivities: completedThisWeek,
    completedCount,
    totalCount,
    progressPercent,
    isLoading,
    needsWeeklyReset,
    hasSeenFeature: state.hasSeenFeature,
    toggleActivity,
    addCustomActivity,
    dismissActivity,
    removeCustomActivity,
    resetWeek,
    markFeatureSeen,
    getWeeklySummary,
  };
}
